import Titulo from '../../components/tipografia/Titulo';
import styles from './page.module.css';
import LayoutUsuario from '../../components/navegacao/LayoutUsuario';
import Descricao from '../../components/tipografia/Descricao';
import HBox from '../../components/layout/HBox';
import Botao from '../../components/Botao';
import VBox from '../../components/layout/VBox';
import CampoTexto from '../../components/Formulario/CampoTexto';
import Icon from '@mdi/react';
import {
    mdiMagnify,
    mdiPlus,
    mdiDelete,
    mdiContentSaveOutline,
    mdiPencil,
    mdiClose,
    mdiTrashCan,
    mdiContentSave,
} from '@mdi/js';
import { useMutation, useQuery } from 'react-query';
import axios from 'axios';
import Grid from '../../components/layout/Grid';
import React from 'react';
import { useDebounce } from '../chat/useDebounce';
import Skeleton from '../../components/layout/Skeleton';
import { toast } from 'react-toastify';
import Modal from '../../components/layout/Modal';
import AlertBox from '../../components/layout/AlertBox';
import DataTable, { DataTableHeader } from '../../components/layout/DataTable';
import FormEstruturaDados from './FormEstruturaDados';
import { set } from 'date-fns';

type PageProps = {};

const Page = ({ }: PageProps) => {
    const [search, setSearch] = React.useState<string>('');
    const [selectedEstrutura, setSelectedEstrutura] = React.useState<any>(null);
    const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
    const [showDeleteModal, setShowDeleteModal] = React.useState<number | null>(null);
    const [showFormModal, setShowFormModal] = React.useState<boolean>(false);
    const debouncedSearch = useDebounce(search, 500);

    // Carregar todas as estruturas de dados
    const query = useQuery(
        ['estrutura-dados:search', debouncedSearch],
        async () => {
            const res = await axios.get('/api/estrutura-dados', {
                params: {
                    txt: debouncedSearch || undefined,
                    t: 50,
                },
            });
            return res.data;
        },
    );

    const changeMutation = useMutation(
        async (estrutura: any) => {
            setFieldErrors({}); // Limpar erros de campo antes de salvar
            if (estrutura.id) {
                // Atualizar estrutura existente
                return await axios.put(`/api/estrutura-dados/${estrutura.id}`, estrutura);
            } else {
                // Criar nova estrutura
                return await axios.post('/api/estrutura-dados', estrutura);
            }
        },
        {
            onSuccess: () => {
                toast('Estrutura de dados salva com sucesso', {
                    type: 'success',
                    position: 'top-center',
                    autoClose: 5000,
                });
                query.refetch();
                setShowFormModal(false);
                setSelectedEstrutura(null);
            },
            onError: (error: any) => {
                setFieldErrors(error.response?.data?.fieldErrors || {});
                toast(error.response?.data?.msg ?? 'Erro ao salvar estrutura de dados', {
                    type: 'error',
                    position: 'top-center',
                    autoClose: 5000,
                });
            },
        },
    );

    // Mutação para excluir uma estrutura de dados
    const deleteMutation = useMutation(
        async (id: number) => {
            return await axios.delete(`/api/estrutura-dados/${id}`);
        },
        {
            onSuccess: () => {
                toast('Estrutura de dados excluída com sucesso', {
                    type: 'success',
                    position: 'top-center',
                    autoClose: 5000,
                });
                query.refetch();
                setShowDeleteModal(null);
            },
            onError: (error: any) => {
                toast(error.response?.data?.msg || 'Erro ao excluir estrutura de dados', {
                    type: 'error',
                    position: 'top-center',
                    autoClose: 5000,
                });
            },
        },
    );

    const handleOpenDelete = (id: number) => {
        setShowDeleteModal(id);
    };

    const handleCloseDelete = () => {
        setShowDeleteModal(null);
    };

    const handleConfirmDelete = () => {
        if (showDeleteModal) {
            deleteMutation.mutate(showDeleteModal);
        }
    };

    const handleOpenForm = (estrutura: any = null) => {
        setSelectedEstrutura(estrutura);
        setShowFormModal(true);
    };

    const handleCloseForm = () => {
        setSelectedEstrutura(null);
        setShowFormModal(false);
    };

    const handleFormConfirm = () => {
        if (selectedEstrutura) {
            changeMutation.mutate(selectedEstrutura);
        } else {
            toast('Nenhuma estrutura selecionada para salvar', {
                type: 'warning',
                position: 'top-center',
                autoClose: 5000,
            });
        }
    };

    const renderContent = () => {
        if (query.isLoading) {
            return (
                <VBox gap={2}>
                    <Skeleton format="box" />
                    <Skeleton format="box" />
                    <Skeleton format="box" />
                </VBox>
            );
        }

        if (query.error) {
            return (
                <AlertBox
                    variant="danger"
                >
                    <p>Erro ao carregar estruturas de dados: {(query.error as any)?.message || 'Erro desconhecido'}</p>
                </AlertBox>
            );
        }

        const headers: DataTableHeader[] = [
            { label: 'Nome', width: '50%', column: 'nome' },
            { label: 'Descrição', width: '50%', column: 'descricao' },
            // {
            //     label: 'Qtd. Metadados',
            //     render: (item: any) => <span>{item.metadados?.length || 0}</span>
            // },
            {
                label: 'Ações',
                width: '120px',
                render: (item: any) => (
                    <HBox gap={8}>
                        <Botao
                            variant="none-primary"
                            onClick={() => handleOpenForm(item)}
                            title="Editar"
                            icon={mdiPencil}
                        />
                        <Botao
                            variant="none-danger"
                            onClick={() => handleOpenDelete(item.id)}
                            title="Apagar"
                            icon={mdiTrashCan}
                        />
                    </HBox>
                )
            },
        ];

        return (
            <DataTable
                cabecalhos={headers}
                linhas={query.data.result}
            />
        );
    };

    return (
        <LayoutUsuario>
            <div className={styles.container}>
                <VBox gap={24}>
                    <HBox>
                        <div className={styles.headerTitulo}>
                            <Titulo>Estruturas de dados</Titulo>
                            <Descricao>
                                Gerencie estruturas de dados para encapsular e desencapsular objetos em fluxos de diálogo.
                            </Descricao>
                        </div>
                        <div className={styles.headerAcoes}>
                            <Botao
                                variant="primary"
                                onClick={() => handleOpenForm({ nome: '', descricao: '', metadados: [] })}
                                title="Nova estrutura"
                                icon={mdiPlus}
                            >
                                Nova estrutura
                            </Botao>
                        </div>
                    </HBox>

                    <CampoTexto
                        nome="search"
                        rotulo=""
                        placeholder="Buscar estrutura de dados..."
                        valor={search}
                        onChange={setSearch}
                        icone={mdiMagnify}
                    />

                    <div>{renderContent()}</div>
                </VBox>
            </div>

            {/* Modal de exclusão */}
            <Modal
                instanceId='estrutura-dados-delete-modal'
                show={!!showDeleteModal}
                onClose={handleCloseDelete}
                size="sm"
                title="Confirmar exclusão"
                cancelProps={{ label: "Cancelar" }}
                confirmProps={{
                    label: "Confirmar",
                    variant: "danger",
                    disabled: deleteMutation.isLoading
                }}
                onConfirm={handleConfirmDelete}
                onCancel={handleCloseDelete}
            >
                <p>Tem certeza que deseja excluir esta estrutura de dados?</p>
                <p>Esta ação não pode ser desfeita.</p>
            </Modal>

            {/* Modal de formulário */}
            <Modal
                instanceId='estrutura-dados-form-modal'
                show={showFormModal}
                onClose={handleCloseForm}
                onCancel={handleCloseForm}
                onConfirm={handleFormConfirm}
                size="lg"
                title={selectedEstrutura?.id > 0 ? "Editar estrutura de dados" : "Nova estrutura de dados"}
                confirmProps={{
                    label: "Salvar",
                    variant: "primary",
                    disabled: changeMutation.isLoading
                }}
                cancelProps={{ label: "Cancelar" }}
            >
                <FormEstruturaDados
                    value={selectedEstrutura}
                    onChange={setSelectedEstrutura}
                    fieldErrors={fieldErrors}
                />
            </Modal>
        </LayoutUsuario>
    );
};

export default Page;
